import { useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { checkDiscount, createCheckout } from "../lib/api";
import { Minus, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartSubtotal } = useStore();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyCode = async () => {
    if (!code) return;
    try { const d = await checkDiscount(code); setDiscount(d); toast.success(`${d.percent * 100}% off applied`); }
    catch { setDiscount(null); toast.error("Invalid discount code"); }
  };

  const discountAmt = discount ? cartSubtotal * discount.percent : 0;
  const shipping = cartSubtotal >= 150 || cartSubtotal === 0 ? 0 : 12;
  const total = cartSubtotal - discountAmt + shipping;

  const checkout = async () => {
    setLoading(true);
    try {
      const items = cart.map((c) => ({ product_id: c.product_id, color: c.color, size: c.size, quantity: c.quantity }));
      const res = await createCheckout({ items, origin_url: window.location.origin, discount_code: discount?.code });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error("Checkout failed. Please try again."); setLoading(false); }
  };

  if (cart.length === 0) return (
    <div className="container-luxe py-32 text-center" data-testid="cart-page-empty">
      <h1 className="h-display text-5xl mb-6">Your Bag Is Empty</h1>
      <Link to="/shop" className="btn-primary">Shop Collection</Link>
    </div>
  );

  return (
    <div className="container-luxe py-16" data-testid="cart-page">
      <h1 className="h-display text-5xl sm:text-6xl mb-12">Your Bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <ul className="divide-y divide-hairline border-t border-hairline">
            {cart.map((item, i) => (
              <li key={i} className="flex gap-6 py-8" data-testid={`cart-page-item-${i}`}>
                <Link to={`/product/${item.slug}`} className="shrink-0"><img src={item.image} alt={item.name} className="h-40 w-32 object-cover bg-beige/40" /></Link>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <Link to={`/product/${item.slug}`} className="font-serif text-2xl">{item.name}</Link>
                    <span className="font-display">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                  <p className="overline text-[10px] text-muted-foreground mt-2">{[item.color, item.size].filter(Boolean).join(" · ")}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center border border-hairline">
                      <button onClick={() => updateQty(i, item.quantity - 1)} className="px-3 py-2"><Minus size={14} /></button>
                      <span className="px-4 text-sm">{item.quantity}</span>
                      <button onClick={() => updateQty(i, item.quantity + 1)} className="px-3 py-2"><Plus size={14} /></button>
                    </div>
                    <button onClick={() => removeFromCart(i)} className="flex items-center gap-2 overline text-[10px] text-muted-foreground hover:text-destructive"><Trash2 size={14} /> Remove</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-beige/40 p-8 sticky top-28">
            <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
            <div className="flex gap-2 mb-6">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" data-testid="discount-input" className="flex-1 border border-charcoal/20 px-3 py-2 bg-transparent outline-none text-sm" />
              <button onClick={applyCode} className="btn-secondary px-5 py-2 text-[10px]" data-testid="apply-discount">Apply</button>
            </div>
            <div className="space-y-3 text-sm border-t border-charcoal/10 pt-5">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="summary-subtotal">${cartSubtotal.toFixed(2)}</span></div>
              {discount && <div className="flex justify-between text-gold"><span>Discount ({discount.code})</span><span>-${discountAmt.toFixed(2)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between font-display text-lg border-t border-charcoal/10 pt-4 mt-2"><span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span></div>
            </div>
            <button onClick={checkout} disabled={loading} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="checkout-btn">
              <Lock size={14} /> {loading ? "Redirecting…" : "Secure Checkout"}
            </button>
            <p className="text-[11px] text-muted-foreground text-center mt-4 font-light">Try code <span className="text-gold">WELCOME10</span> for 10% off</p>
          </div>
        </div>
      </div>
    </div>
  );
}
